import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Accounting } from '~/types/accounting';
import { IntegrationStatus } from '~/types/api.model';
export class AccountingIntegrationModel {
    static fromQBO(dto) {
        return new AccountingIntegrationModel(_object_spread({
            id: dto.id,
            companyId: dto.id,
            status: dto.status,
            source: dto.source
        }, dto.qbo ? {
            companyName: dto.qbo.companyName,
            linkUrl: dto.qbo.linkUrl,
            isReady: dto.qbo.isReady,
            settings: dto.qbo.settings,
            qboSettings: dto.qbo.qboSettings,
            accounts: dto.qbo.accounts,
            multipleAccountsConnected: dto.qbo.multipleAccountsConnected
        } : {
            companyName: '',
            linkUrl: '',
            isReady: false,
            settings: {
                accounting: Accounting.QBO,
                automaticInvoicesSync: false,
                classTrackingAgreementOverride: false,
                displayBillableTimeActivities: false
            },
            qboSettings: {
                customTxnNumbers: false
            },
            accounts: {},
            multipleAccountsConnected: false
        }));
    }
    static fromXero(dto) {
        return new AccountingIntegrationModel(_object_spread({
            id: dto.id,
            status: dto.status,
            source: dto.source
        }, dto.xero ? {
            companyName: dto.xero.companyName,
            isReady: dto.xero.isReady,
            settings: dto.xero.settings,
            accounts: dto.xero.accounts,
            multipleAccountsConnected: dto.xero.multipleAccountsConnected
        } : {
            companyName: '',
            isReady: false,
            settings: {
                accounting: Accounting.Xero,
                automaticInvoicesSync: false
            },
            accounts: {},
            multipleAccountsConnected: false
        }));
    }
    get source() {
        return this.data.source;
    }
    get status() {
        return this.data.status;
    }
    toXeroDTO() {
        return this.data.status === IntegrationStatus.Linked ? {
            id: this.data.id,
            status: IntegrationStatus.Linked,
            source: Accounting.Xero,
            xero: {
                companyName: this.data.companyName,
                isReady: this.data.isReady,
                settings: _object_spread({}, this.data.settings),
                accounts: this.data.accounts,
                multipleAccountsConnected: this.data.multipleAccountsConnected
            }
        } : {
            id: this.data.id,
            status: this.data.status,
            source: Accounting.Xero
        };
    }
    toQboDTO() {
        if (this.data.status === IntegrationStatus.Linked) {
            var _this_data_qboSettings;
            return {
                id: this.data.id,
                status: IntegrationStatus.Linked,
                source: Accounting.QBO,
                qbo: {
                    companyName: this.data.companyName,
                    isReady: this.data.isReady,
                    settings: {
                        automaticInvoicesSync: this.automaticInvoicesSync(),
                        classTrackingAgreementOverride: this.classTrackingAgreementOverride(),
                        displayBillableTimeActivities: this.displayBillableTimeActivities()
                    },
                    qboSettings: {
                        customTxnNumbers: this.data.source === Accounting.QBO ? (_this_data_qboSettings = this.data.qboSettings) === null || _this_data_qboSettings === void 0 ? void 0 : _this_data_qboSettings.customTxnNumbers : false
                    },
                    accounts: this.data.accounts,
                    multipleAccountsConnected: this.data.multipleAccountsConnected
                }
            };
        } else {
            return {
                id: this.data.id,
                status: this.data.status,
                source: Accounting.QBO
            };
        }
    }
    toQBOIntegration() {
        if (this.data.status === IntegrationStatus.Linked) {
            var _this_data_qboSettings;
            return {
                id: this.data.id,
                status: IntegrationStatus.Linked,
                source: Accounting.QBO,
                companyName: this.data.companyName,
                companyId: this.data.id,
                linkUrl: this.linkUrl(),
                isReady: this.data.isReady,
                settings: {
                    automaticInvoicesSync: this.automaticInvoicesSync(),
                    classTrackingAgreementOverride: this.classTrackingAgreementOverride(),
                    displayBillableTimeActivities: this.displayBillableTimeActivities()
                },
                qboSettings: {
                    customTxnNumbers: this.data.source === Accounting.QBO ? (_this_data_qboSettings = this.data.qboSettings) === null || _this_data_qboSettings === void 0 ? void 0 : _this_data_qboSettings.customTxnNumbers : false
                },
                accounts: this.data.accounts,
                multipleAccountsConnected: this.data.multipleAccountsConnected
            };
        } else {
            return {
                id: this.data.id,
                status: this.data.status,
                source: Accounting.QBO,
                companyName: '',
                companyId: '',
                isReady: false,
                settings: {
                    automaticInvoicesSync: false,
                    classTrackingAgreementOverride: false,
                    displayBillableTimeActivities: false
                },
                qboSettings: {
                    customTxnNumbers: false
                },
                accounts: {},
                multipleAccountsConnected: false
            };
        }
    }
    isLinked() {
        return this.status === IntegrationStatus.Linked;
    }
    getLinkUrl() {
        if (this.data.source === Accounting.QBO) {
            var _this_data_linkUrl;
            return (_this_data_linkUrl = this.data.linkUrl) !== null && _this_data_linkUrl !== void 0 ? _this_data_linkUrl : '';
        }
        return '';
    }
    settings() {
        return this.data.settings;
    }
    isPendingAccountingIntegrationSetup() {
        const accountsList = Object.values(this.data.accounts);
        const isValidAccountsLength = accountsList.length === 3;
        const areAllAccountsSynced = accountsList.every((account)=>Boolean(account.synced));
        return !isValidAccountsLength || !areAllAccountsSynced;
    }
    displayBillableTimeActivities() {
        switch(this.data.source){
            case Accounting.QBO:
                return this.data.settings.displayBillableTimeActivities;
            case Accounting.Xero:
                return false;
        }
    }
    classTrackingAgreementOverride() {
        switch(this.data.source){
            case Accounting.QBO:
                return this.data.settings.classTrackingAgreementOverride;
            case Accounting.Xero:
                return false;
        }
    }
    linkUrl() {
        switch(this.data.source){
            case Accounting.QBO:
                var _this_data_linkUrl;
                return (_this_data_linkUrl = this.data.linkUrl) !== null && _this_data_linkUrl !== void 0 ? _this_data_linkUrl : '';
            case Accounting.Xero:
                return '';
        }
    }
    automaticInvoicesSync() {
        return this.data.settings.automaticInvoicesSync;
    }
    isReady() {
        return this.data.isReady;
    }
    companyName() {
        return this.data.companyName;
    }
    isMultipleAccountsConnected() {
        return this.data.multipleAccountsConnected;
    }
    getClearingAccountId() {
        var _ref;
        var _this_data_accounts_clearing_synced, _this_data_accounts_clearing;
        return (_ref = (_this_data_accounts_clearing = this.data.accounts.clearing) === null || _this_data_accounts_clearing === void 0 ? void 0 : (_this_data_accounts_clearing_synced = _this_data_accounts_clearing.synced) === null || _this_data_accounts_clearing_synced === void 0 ? void 0 : _this_data_accounts_clearing_synced.integratedId) !== null && _ref !== void 0 ? _ref : undefined;
    }
    getBankAccountId() {
        var _ref;
        var _this_data_accounts_bank_synced, _this_data_accounts_bank;
        return (_ref = (_this_data_accounts_bank = this.data.accounts.bank) === null || _this_data_accounts_bank === void 0 ? void 0 : (_this_data_accounts_bank_synced = _this_data_accounts_bank.synced) === null || _this_data_accounts_bank_synced === void 0 ? void 0 : _this_data_accounts_bank_synced.integratedId) !== null && _ref !== void 0 ? _ref : undefined;
    }
    getExpenseAccountId() {
        var _ref;
        var _this_data_accounts_expense_synced, _this_data_accounts_expense;
        return (_ref = (_this_data_accounts_expense = this.data.accounts.expense) === null || _this_data_accounts_expense === void 0 ? void 0 : (_this_data_accounts_expense_synced = _this_data_accounts_expense.synced) === null || _this_data_accounts_expense_synced === void 0 ? void 0 : _this_data_accounts_expense_synced.integratedId) !== null && _ref !== void 0 ? _ref : undefined;
    }
    isErrored() {
        return this.status === IntegrationStatus.Error;
    }
    setClassTrackingAgreementOverride(classTrackingAgreementOverride) {
        switch(this.data.source){
            case Accounting.QBO:
                return new AccountingIntegrationModel(_object_spread_props(_object_spread({}, this.data), {
                    settings: _object_spread_props(_object_spread({}, this.data.settings), {
                        classTrackingAgreementOverride
                    })
                }));
            case Accounting.Xero:
                return this;
        }
    }
    setDisplayBillableTimeActivities(displayBillableTimeActivities) {
        switch(this.data.source){
            case Accounting.QBO:
                return new AccountingIntegrationModel(_object_spread_props(_object_spread({}, this.data), {
                    settings: _object_spread_props(_object_spread({}, this.data.settings), {
                        displayBillableTimeActivities
                    })
                }));
            case Accounting.Xero:
                return this;
        }
    }
    setAutomaticInvoicesSync(automaticInvoicesSync) {
        switch(this.data.source){
            case Accounting.QBO:
                return new AccountingIntegrationModel(_object_spread_props(_object_spread({}, this.data), {
                    settings: _object_spread_props(_object_spread({}, this.data.settings), {
                        automaticInvoicesSync
                    })
                }));
            case Accounting.Xero:
                return new AccountingIntegrationModel(_object_spread_props(_object_spread({}, this.data), {
                    settings: _object_spread_props(_object_spread({}, this.data.settings), {
                        automaticInvoicesSync
                    })
                }));
        }
    }
    settingValue(id) {
        return this.data.settings[id] || false;
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
